import { useState } from "react";
import { Icon } from "./chrome.tsx";
import { num } from "../lib/format.ts";
import { t } from "../i18n/lang.ts";
import { UI } from "../i18n/dict.ts";
import { ApiError } from "../lib/api.ts";
import { EXPORT_MAX_ROWS } from "../lib/tableExport.ts";

/**
 * Кнопка «Excel» в шапке панели со списком под цифрой.
 *
 * Выгрузка списка в несколько тысяч строк — это десятки запросов по 500
 * строк, и молчащая кнопка на десять секунд выглядит сломанной. Поэтому пока
 * идёт сбор, на кнопке счёт «собрано из всего», а ошибка пишется рядом
 * словами, а не пропадает в консоли.
 */
const TX = {
  label: { ru: "Excel", en: "Excel" },
  hint: { ru: "Выгрузить этот список в Excel целиком", en: "Export this whole list to Excel" },
  collecting: { ru: "Собираю", en: "Collecting" },
  of: { ru: "из", en: "of" },
  writing: { ru: "Готовлю файл…", en: "Preparing the file…" },
  tooLarge: {
    ru: `В списке больше ${num(EXPORT_MAX_ROWS)} строк — сузьте период или фильтр`,
    en: `The list has more than ${EXPORT_MAX_ROWS.toLocaleString("en-US")} rows — narrow the period or the filter`,
  },
  failed: { ru: "Не удалось выгрузить", en: "Export failed" },
};

export function ExportButton({
  run,
  disabled = false,
}: {
  /** Собирает строки и пишет файл; `progress` — сколько собрано из всего. */
  run: (progress: (done: number, total: number) => void) => Promise<void>; // i18n-ok: тип TypeScript, а не текст на экране
  disabled?: boolean;
}): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await run((d, total) => setDone({ done: d, total }));
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
      setError(/^\d+ > \d+$/.test(msg) ? t(TX.tooLarge) : `${t(TX.failed)}: ${msg}`);
    } finally {
      setBusy(false);
      setDone(null);
    }
  };

  const label = !busy
    ? t(TX.label)
    : done !== null && done.done < done.total
      ? `${t(TX.collecting)} ${num(done.done)} ${t(TX.of)} ${num(done.total)}`
      : t(TX.writing);

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
      <button
        type="button"
        className="btn-ghost"
        onClick={() => void start()}
        disabled={busy || disabled}
        title={t(TX.hint)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
      >
        <Icon name="export" size={14} width={2.2} />
        {label}
      </button>
      {error !== null && (
        <span className="sub" style={{ color: "var(--bad, #d9534f)", maxWidth: 260, textAlign: "right" }}>
          {error}
        </span>
      )}
    </span>
  );
}

/**
 * Правая часть шапки панели: «Excel» и «закрыть». Одна на все панели со
 * списками, чтобы кнопка стояла везде одинаково — справа сверху.
 */
export function DrawerActions({
  onClose,
  exportRun,
  exportDisabled = false,
}: {
  onClose: () => void;
  /** Нет — кнопки выгрузки нет (например, пока в «Закрытых депозитах» не выбран месяц). */
  exportRun?: (progress: (done: number, total: number) => void) => Promise<void>; // i18n-ok: тип TypeScript, а не текст на экране
  exportDisabled?: boolean;
}): React.ReactElement {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flex: "none" }}>
      {exportRun !== undefined && <ExportButton run={exportRun} disabled={exportDisabled} />}
      <button type="button" className="icon-btn" onClick={onClose} aria-label={t(UI.close)} title={t(UI.close)}>
        <Icon name="close" size={16} width={2.4} />
      </button>
    </div>
  );
}
